import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ComponentRef, computed, ElementRef, Inject, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AnnotationPopupComponent, PopupTag } from 'src/app/components/shared/labeler/annotation-popup/annotation-popup.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RiskReportComponent } from 'src/app/components/project-pages/risks/risk-report/risk-report.component';
import { OptionalFieldsComponent } from 'projects/ui-library/src/lib/components/optional-fields/optional-fields.component';
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { first, Subject, takeUntil } from 'rxjs';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { BaseDrawerComponent, DrawerBox, RiskBox } from 'src/app/components/shared/labeler/base-drawer/base-drawer.component';
import { Attachment, PutAttachment } from 'src/app/interfaces/api/attachment';
import { Image } from 'src/app/interfaces/api/image';
import { ImageStatus, LabelUpdate, OptionalField, StatusLabel } from 'src/app/interfaces/api/imageStatus';
import { Label } from 'src/app/interfaces/api/label';
import { LabelProject } from 'src/app/interfaces/api/labelProject';
import { Risk } from 'src/app/interfaces/api/metric';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { Tag } from 'src/app/interfaces/shared/tag';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { ImageStatusService } from 'src/app/services/api/imageStatus/imageStatus.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { WebSocketService } from 'src/app/services/core/web-socket.service';
import { ButtonComponent, SearchBarComponent } from 'ui-library';

/** One tagged risk on the image (its own box, label, fields, comment, shared _id). */
interface RiskTag {
  _id?: string;
  box: RiskBox;
  label?: string;
  module?: string;
  color?: string;
  author?: string;
  // set for AI predictions only (< 1), cleared once the user edits the tag
  probability?: number;
  optionalFields: OptionalField[];
  comment?: string;
  isNew?: boolean;
}

interface DialogData {
  image: Image;
  tracker?: Tracker,
  project: Project,
  user: User,
  labelProjects: LabelProject[],
  labels: Label[],
  galleryTool: GalleryTool,
  getImageInfo: Function,
  canGoLeft: boolean,
  canGoRight: boolean,
  canEdit: boolean;
  risk?: Risk;
  refreshImage?: (imageId: string) => string;
  withRand?: (url: string | undefined, imageId: string) => string | undefined;
}

@Component({
  selector: 'app-labeler',
  imports: [
    CommonModule,
    MatDialogModule,
    BaseDrawerComponent,
    ButtonComponent,
    TranslateModule,
    TimeFormatPipe,
    SearchBarComponent
  ],
  templateUrl: './labeler.component.html',
  styleUrls: ['./labeler.component.scss']
})
export class LabelerComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;
  @ViewChild('drawerSection') drawerSection!: ElementRef<HTMLElement>;
  @ViewChild('formsSection') formsSection!: ElementRef<HTMLElement>;
  @ViewChild(BaseDrawerComponent) baseDrawer?: BaseDrawerComponent;

  // Box-anchored optional-fields popup (CDK overlay)
  private overlay = inject(Overlay);
  private overlayRef: OverlayRef | null = null;
  private popupRef: ComponentRef<AnnotationPopupComponent> | null = null;
  private popupPositionStrategy: FlexibleConnectedPositionStrategy | null = null;
  private popupSubs: Subscription[] = [];
  private popupWasOpen = false;   // remembers popup state across a drag/resize
  @ViewChild('optFields', { static: false }) optFields?: OptionalFieldsComponent;

  @Input() attachment!: Attachment;
  @Input() image!: Image;
  @Input() labels: Label[] = [];

  visibleLabels = signal<Label[]>([]);
  selectedLabel = signal<Label | undefined>(undefined);
  armedLabel = signal<Label | undefined>(undefined);
  pendingBoxLabel = computed(() => {
    const label = this.armedLabel();
    return label ? { label: label.displayName || label.name, color: label.color } : null;
  });
  labelColorMap = signal<Record<string, string>>({});

  imageStatusList!: ImageStatus[];
  optionalFields: OptionalField[] = [];
  // Shared id carried by both the imageStatus label and the attachment risk tag (RG-13),
  // preserved on edit so the tag ↔ label ↔ Event/Metric linkage stays stable.
  labelId?: string;
  box: RiskBox | null = null;
  // Multi-risk: all tagged boxes on the image; the single fields above mirror tags[activeIndex].
  tags: RiskTag[] = [];
  activeIndex: number | null = null;
  riskName = '';
  criticality = '';
  description = '';
  comment = '';
  labelProjects: LabelProject[] = [];
  deletedTag: boolean = false;
  hasChange = false;
  user!: User;
  galleryTool!: GalleryTool;
  searchText: string = "";
  imageInfo!: DialogData;
  canGoLeft: boolean = true;
  canGoRight: boolean = true;
  canEdit: boolean = false;
  risk?: Risk;
  private destroy$ = new Subject<void>();
  showScrollUp = false;
  showScrollDown = false;

  constructor(
    private imageStatusService: ImageStatusService,
    public dialogRef: MatDialogRef<LabelerComponent>,
    @Inject(MAT_DIALOG_DATA) public _data: DialogData,
    private cdr: ChangeDetectorRef,
    private attachmentService: AttachmentService,
    private metricService: MetricService,
    private dialog: MatDialog,
    private ws: WebSocketService,
    private pop: Pop,
    private tools: Tools,
    private translate: TranslateService
  ) {
    if (_data) {
      this.init(_data);
    }
  }

  ngOnInit(): void {
    this.buildLabelColorMap();
    this.dialogRef.beforeClosed().subscribe(() => {
      this.optFields?.reset();
    });

    this.ws.getUpdateRaw("ImageStatus").pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      if (msg.method !== "UPDATE") return;
      const statuses: ImageStatus[] = msg.data;
      const updated = statuses.find(s => {
        const imageId = typeof s.image === 'string' ? s.image : (s.image as any)?.id;
        return imageId === this.image?.id;
      });
      if (!updated) return;
      this.imageStatusList = this.image.statuses = statuses;
      if (!this.imageStatus?.labels?.length) this.risk = undefined;
      // rebuild the tag list from the server unless the user is mid-edit
      if (!this.hasChange) {
        this.keepHoverPreview();
        this.tags = this.buildTags();
        this.activeIndex = null;
        this.clearActiveFields();
        this.destroyPopup();
      }
      this.cdr.detectChanges();
    });

    this.ws.getUpdateRaw("Image").pipe(takeUntil(this.destroy$)).subscribe((msg) => {
      if (msg.method === "DELETE") {
        const deletedIds = new Set(msg.data.map((d: any) => d.id));
        if (deletedIds.has(this.image?.id)) {
          this.pop.toast(this.translate.instant('stream.upload.remote_delete'), 'success');
          this.dialogRef.close();
        }
        return;
      }
      const updated = msg.data.find((img: any) => img.id === this.image?.id);
      if (!updated) return;
      this.image = { ...this.image, ...updated };
      this.imageStatusList = this.image.statuses || [];
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.cancelHoverClose();
    this.destroyPopup();
    this.destroy$.next();
    this.destroy$.complete();
  }


  init(_data: DialogData) {
    this.optFields?.reset();
    this.keepHoverPreview();
    this.destroyPopup();
    this.imageInfo = _data;
    this.box = null;
    this.hasChange = false;
    this.selectedLabel.set(undefined);
    this.image = _data.image;
    this.canEdit = _data.canEdit;
    this.labelProjects = _data.labelProjects;
    this.imageStatusList = this.image?.statuses || [];
    this.labels = _data.labels;
    this.visibleLabels.set(this.labels);
    this.user = _data.user;
    this.galleryTool = _data.galleryTool;
    this.tags = this.buildTags();
    this.activeIndex = null;
    this.clearActiveFields();
    this.canGoLeft = _data.canGoLeft;
    this.canGoRight = _data.canGoRight;
    this.risk = _data.risk;
    this.viewAction();
  }

  // ---- multi-risk helpers -------------------------------------------------

  get imageUrl(): string {
    return this.imageInfo?.withRand?.(this.image?.url, this.image?.id) ?? this.image?.url;
  }

  // Main label project (risk): drives the popup fields, criticity and the risk report.
  get labelProject(): LabelProject | undefined {
    return this.labelProjects[0];
  }

  get imageStatus(): ImageStatus | undefined {
    return this.statusOf(this.labelProject);
  }

  private statusOf(labelProject?: LabelProject): ImageStatus | undefined {
    return this.imageStatusList?.find(status => status.labelProject == labelProject?.id);
  }

  private buildTags(): RiskTag[] {
    return this.labelProjects.flatMap(lp => {
      const status = this.statusOf(lp);
      const aiLabels = (status?.aiLabels || []).filter(l => this.isShownAi(l, lp));
      return [...(status?.labels || []), ...aiLabels].map(l => this.statusLabelToTag(l, lp.name));
    });
  }

  // AI predictions are only shown for the main (risk) project, when they match one of its labels.
  private isShownAi(l: StatusLabel, labelProject: LabelProject): boolean {
    return labelProject === this.labelProject && this.labels.some(label => label.name === l.name && label.module === labelProject.name);
  }

  // the label's own module wins, so a box stored under the wrong status moves back where it belongs
  private moduleOf(t: RiskTag): string | undefined {
    return this.labels.find(l => l.name === t.label)?.module ?? t.module ?? this.labelProject?.name;
  }

  private isRiskTag(t?: RiskTag): boolean {
    return !!t && this.moduleOf(t) === this.labelProject?.name;
  }

  private tagTitle(t: RiskTag): string {
    const name = this.displayName(t.label);
    return t.probability != null ? `${name} ${Math.round(t.probability * 100)}%` : name;
  }

  private statusLabelToTag(l: StatusLabel, module: string): RiskTag {
    const [left = 0, top = 0, width = 0, height = 0] = l.boundingBox || [];
    const fields = l.optionalFields || [];
    return {
      _id: l._id,
      box: { left, top, width, height },
      label: l.name,
      module,
      probability: (l.probability ?? 1) < 1 ? l.probability : undefined,
      color: this.labelColor(l.name),
      author: l.author,
      optionalFields: fields.filter(f => f.key !== 'commentaire'),
      comment: fields.find(f => f.key === 'commentaire')?.value?.[0] || ''
    };
  }

  private labelColor(name?: string): string | undefined {
    return this.labels.find(l => l.name === name)?.color;
  }

  private displayName(name?: string): string {
    if (!name) return '';
    return this.labels.find(l => l.name === name)?.displayName || name;
  }

  // Memoized: this feeds the child base-drawer's [boxes] input, so returning a fresh
  // array on every change-detection read left the child perpetually "changed" and
  // spun change detection forever (frozen tab). Rebuild only when `tags` is actually
  // reassigned (init / edits / server refresh all do `this.tags = ...`).
  private _drawerBoxesTags: RiskTag[] | null = null;
  private _drawerBoxesCache: DrawerBox[] = [];
  get drawerBoxes(): DrawerBox[] {
    if (this._drawerBoxesTags !== this.tags) {
      this._drawerBoxesTags = this.tags;
      this._drawerBoxesCache = this.tags.map(t => ({ box: t.box, label: this.tagTitle(t), color: t.color, ai: t.probability != null }));
    }
    return this._drawerBoxesCache;
  }

  // Same memoization as drawerBoxes — avoids rebuilding the risk chips every CD.
  private _riskNamesTags: RiskTag[] | null = null;
  private _riskNamesCache: { index: number; name: string }[] = [];
  get riskNames(): { index: number; name: string }[] {
    if (this._riskNamesTags !== this.tags) {
      this._riskNamesTags = this.tags;
      this._riskNamesCache = this.tags
        .map((t, index) => ({ index, name: this.tagTitle(t) || '—', isRisk: this.isRiskTag(t) }))
        .filter(r => r.isRisk);
    }
    return this._riskNamesCache;
  }

  private get activeTag(): RiskTag | undefined {
    return this.activeIndex != null ? this.tags[this.activeIndex] : undefined;
  }

  /** Load the single mirror fields from the active tag. */
  private loadActiveFields(): void {
    const t = this.activeTag;
    this.box = t ? t.box : null;
    this.labelId = t?._id;
    this.optionalFields = t ? [...t.optionalFields] : [];
    this.comment = t?.comment || '';
    this.selectedLabel.set(t?.label ? this.labels.find(l => l.name === t.label) : undefined);
  }

  private clearActiveFields(): void {
    this.activeIndex = null;
    this.box = null;
    this.labelId = undefined;
    this.optionalFields = [];
    this.comment = '';
    this.selectedLabel.set(undefined);
  }

  /** Write the single mirror fields back into the active tag. */
  private saveActiveFields(): void {
    const t = this.activeTag;
    if (!t) return;
    t.optionalFields = [...this.optionalFields];
    t.comment = this.comment;
    t._id = this.labelId;
    const label = this.selectedLabel();
    if (label) { t.label = label.name; t.module = label.module; t.color = label.color; }
    this._drawerBoxesTags = null;
    this._riskNamesTags = null;
  }

  parseLabel(label?: StatusLabel) {
    label = label ?? (this.imageStatus?.labels && this.imageStatus.labels[0]);
    if (!label?.boundingBox) return;
    let [left = 0, top = 0, width = 0, height = 0] = label.boundingBox;
    this.box = { left, top, width, height };
  }

  // Client-side 24-hex ObjectId so a new box's tag and label share the same _id (RG-13)
  private genObjectId(): string {
    const ts = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    let rest = '';
    for (let i = 0; i < 16; i++) rest += Math.floor(Math.random() * 16).toString(16);
    return ts + rest;
  }

  viewAction(actionName: string = "view") {
    for (const labelProject of this.labelProjects) {
      const status = this.statusOf(labelProject);
      let query: any = {
        id: status?.id,
        action: actionName,
        labels: status?.labels,
        labelProject: labelProject.id,
        optionalFields: status?.optionalFields,
        project: this.image?.project,
        tracker: this.image.tracker,
        imageId: this.image?.id,
        database: this.image?.database,
        createdDate: this.image?.createdDate
      };

      if (!status) {
        this.imageStatusService.addStatus(query).pipe(first()).subscribe((res) => {
          if (this.image && !this.imageStatusList.find(s => s.id === res.id)) {
            this.imageStatusList.push(res);
            this.image.statuses = this.imageStatusList;
          }
          this.cdr.detectChanges();
        });
      } else if (!status.viewedBy?.includes(this.user?.email)) {
        this.imageStatusService.updateStatus(query).pipe(first()).subscribe((imageStatus) => {
          const statusIndex: number = this.imageStatusList.findIndex(s => s.id == imageStatus?.id);
          if (statusIndex >= 0) {
            this.imageStatusList.splice(statusIndex, 1, imageStatus);
            this.image.statuses = this.imageStatusList;
          }
          this.cdr.detectChanges();
        });
      }
    }
  }

  // Hide the drawer's floating draw/edit buttons once the user scrolls near the bottom action
  // bar, so they don't sit on top of the "Signaler"/save buttons on mobile portrait.
  hideDrawerTools = false;
  onModalScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.hideDrawerTools = el.scrollTop + el.clientHeight >= el.scrollHeight - 130;
  }

  onBoxCreated(box: RiskBox) {
    this.keepHoverPreview();
    this.saveActiveFields();
    const tag: RiskTag = { _id: this.genObjectId(), box, optionalFields: [], author: this.user?.email, isNew: true };
    this.tags = [...this.tags, tag];
    this.activeIndex = this.tags.length - 1;
    this.deletedTag = false;
    this.hasChange = true;
    this.loadActiveFields();
    // Creating a box only selects it — the editor opens on an explicit double-tap/double-click.
    this.destroyPopup();

    const armed = this.armedLabel();
    if (armed) {
      this.applyLabel(armed);
      this.saveActiveFields();
    }
  }

  private hoverPreview: { index: number; previousIndex: number | null } | null = null;
  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly HOVER_CLOSE_DELAY_MS = 250;

  onBoxHovered(index: number) {
    this.cancelHoverClose();
    const tag = this.tags[index];
    if (this.isMobile || tag?.probability == null || index === this.activeIndex) return;
    if (this.popupRef && !this.hoverPreview) return;
    const previousIndex = this.hoverPreview ? this.hoverPreview.previousIndex : this.activeIndex;
    this.saveActiveFields();
    this.activeIndex = index;
    this.loadActiveFields();
    this.hoverPreview = { index, previousIndex };
    this.openOrUpdatePopup();
  }

  scheduleHoverClose() {
    if (!this.hoverPreview) return;
    this.cancelHoverClose();
    this.hoverCloseTimer = setTimeout(() => this.endHoverPreview(), LabelerComponent.HOVER_CLOSE_DELAY_MS);
  }

  private cancelHoverClose() {
    if (this.hoverCloseTimer) clearTimeout(this.hoverCloseTimer);
    this.hoverCloseTimer = null;
  }

  private endHoverPreview() {
    this.hoverCloseTimer = null;
    const preview = this.hoverPreview;
    if (!preview) return;
    this.hoverPreview = null;
    this.destroyPopup();
    if (this.activeIndex !== preview.index) return;
    this.saveActiveFields();
    this.activeIndex = preview.previousIndex != null && preview.previousIndex < this.tags.length ? preview.previousIndex : null;
    this.loadActiveFields();
    this.cdr.detectChanges();
  }

  private keepHoverPreview() {
    this.cancelHoverClose();
    this.hoverPreview = null;
  }

  onBoxSelected(index: number) {
    this.keepHoverPreview();
    if (index === this.activeIndex) { this.refreshPopupIfOpen(); return; }
    this.saveActiveFields();
    this.activeIndex = index;
    this.loadActiveFields();
    // Single click only selects; follow the popup to the new box if one is already open.
    this.refreshPopupIfOpen();
  }

  // Double-click / right-click (desktop) or tap (mobile) on a box → open its editor.
  onBoxOpened(index: number) {
    this.keepHoverPreview();
    if (index !== this.activeIndex) {
      this.saveActiveFields();
      this.activeIndex = index;
      this.loadActiveFields();
    }
    // Explicit gesture: open even when the box has no label yet — the popup/drawer is where
    // the label gets picked (essential on mobile, where there's no separate label list step).
    this.openOrUpdatePopup();
  }

  onBoxChanged(e: { index: number; box: RiskBox }) {
    const tag = this.tags[e.index];
    if (tag) tag.box = e.box;
    if (e.index === this.activeIndex) this.box = e.box;
    this.hasChange = true;
    // Reopen (re-anchored) once the move finishes, but only if it was open before the drag.
    if (this.popupWasOpen) { this.popupWasOpen = false; this.maybeOpenPopup(); }
  }

  // A drag/resize started moving the box — hide the popup until the move ends.
  onInteractionStart() {
    this.keepHoverPreview();
    this.popupWasOpen = !!this.popupRef;
    this.destroyPopup();
  }

  // Open the popup only when there is a completed box AND a label selected for it.
  private maybeOpenPopup(): void {
    if (this.activeIndex == null || !this.activeTag?.label) { this.destroyPopup(); return; }
    this.openOrUpdatePopup();
  }

  // Refresh/re-anchor the popup only if it is already open — never create it.
  private refreshPopupIfOpen(): void {
    if (this.popupRef) this.maybeOpenPopup();
  }

  private buildPopupTag(): PopupTag {
    return {
      label: this.selectedLabel()?.name,
      author: this.activeTag?.author || this.user?.email,
      color: (this.selectedLabel() as any)?.color,
      probability: this.activeTag?.probability ?? 1,
      optionalFields: this.optionalFields,
    };
  }

  // An edited AI prediction becomes a regular user tag.
  private confirmTag(tag?: RiskTag): boolean {
    if (tag?.probability == null) return false;
    tag.probability = undefined;
    tag.author = this.user?.email;
    tag.isNew = true;
    this.tags = [...this.tags];
    this.hasChange = true;
    return true;
  }

  onEditTag(): void {
    this.keepHoverPreview();
    if (this.confirmTag(this.activeTag)) {
      this.applyDynamicDefaults(this.selectedLabel(), false, true);
      this.saveActiveFields();
    }
    this.refreshPopupIfOpen();
  }

  private applyPopupInputs(instance: AnnotationPopupComponent): void {
    instance.tag = this.buildPopupTag();
    instance.labelProject = this.labelProjects.find(lp => lp.name === this.selectedLabel()?.module) ?? this.labelProject!;
    instance.labels = this.labels;
    instance.selectedLabel = this.selectedLabel();
    instance.labelCatalogEntry = this.selectedLabel() || null;
    instance.displayTitle = (this.selectedLabel() as any)?.displayName || this.selectedLabel()?.name || '';
    instance.currentUserEmail = this.user?.email || '';
    instance.labelReadOnly = !this.canEdit;
    instance.comment = this.comment;
    instance.probability = this.activeTag?.probability;
    instance.hideFields = this.activeTag?.probability != null || (!!this.activeTag?.label && !this.isRiskTag(this.activeTag));
  }

  // Open the popup anchored to the active box (or refresh it if already open).
  private openOrUpdatePopup(): void {
    if (this.activeIndex == null || !this.labelProject) { this.destroyPopup(); return; }
    // defer so the box element is rendered and positioned
    setTimeout(() => this.openPopupForActive(true));
  }

  private get isMobile(): boolean {
    return window.innerWidth < 768;
  }

  private popupPositions(): ConnectedPosition[] {
    const sides: ConnectedPosition[] = [
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -14 },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 14 },
    ];
    if (this.activeTag?.probability == null) return sides;
    return [
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -14 },
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 14 },
      ...sides,
    ];
  }

  private openPopupForActive(allowRetry: boolean): void {
    if (this.activeIndex == null || !this.labelProject) { this.destroyPopup(); return; }

    // Mobile has no room for a box-anchored popup — open a right-side drawer below the image.
    if (this.isMobile) { this.openMobileDrawer(); return; }

    const anchor = this.baseDrawer?.getBoxElement(this.activeIndex);
    if (!anchor) {
      // box not laid out yet — try once more on the next frame
      if (allowRetry) requestAnimationFrame(() => this.openPopupForActive(false));
      return;
    }

    // Already open: re-anchor to the (possibly new) active box and refresh its inputs.
    if (this.popupRef && this.overlayRef && this.popupPositionStrategy) {
      this.popupPositionStrategy.setOrigin(anchor).withPositions(this.popupPositions());
      this.applyPopupInputs(this.popupRef.instance);
      this.popupRef.changeDetectorRef.detectChanges();
      this.overlayRef.updatePosition();
      return;
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(anchor)
      .withPositions(this.popupPositions())
      .withFlexibleDimensions(false)
      .withViewportMargin(8)
      .withPush(true);
    this.popupPositionStrategy = positionStrategy;

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    // Keep the pane hidden until it has been positioned against its rendered size —
    // otherwise CDK attaches it at (0,0) and it flashes (or sticks) top-left.
    this.overlayRef.overlayElement.style.visibility = 'hidden';

    this.popupRef = this.overlayRef.attach(new ComponentPortal(AnnotationPopupComponent));
    const instance = this.popupRef.instance;
    this.applyPopupInputs(instance);
    // Render the content so the overlay has real dimensions, then position and reveal it.
    this.popupRef.changeDetectorRef.detectChanges();
    this.overlayRef.updatePosition();
    requestAnimationFrame(() => {
      if (!this.overlayRef) return;
      this.overlayRef.updatePosition();
      this.overlayRef.overlayElement.style.visibility = 'visible';
    });

    this.popupSubs = [
      instance.closed.subscribe(() => this.hoverPreview ? this.endHoverPreview() : this.destroyPopup()),
      instance.hovered.subscribe((inside: boolean) => inside ? this.cancelHoverClose() : this.scheduleHoverClose()),
      instance.deleteTag.subscribe(() => { const i = this.activeIndex; this.destroyPopup(); if (i != null) this.onBoxDeleted(i); }),
      instance.editTag.subscribe(() => this.onEditTag()),
      instance.fieldsChange.subscribe((fields: OptionalField[]) => this.onFieldsChange(fields)),
      instance.commentChange.subscribe((c: string) => this.onCommentChange(c)),
      instance.labelChanged.subscribe((labelId: string) => this.onLabelChanged(labelId)),
      positionStrategy.positionChanges.subscribe(({ connectionPair: pair }) => {
        if (pair.originY === 'top' && pair.overlayY === 'bottom') instance.arrowSide = 'bottom';
        else if (pair.originY === 'bottom' && pair.overlayY === 'top') instance.arrowSide = 'top';
        else instance.arrowSide = pair.overlayX === 'start' ? 'left' : 'right';
        this.popupRef?.changeDetectorRef.detectChanges();
      }),
    ];
  }

  // Mobile: a slide-in drawer pinned to the right, starting just below the image.
  private openMobileDrawer(): void {
    // Already open → just refresh its inputs (switching boxes / label change).
    if (this.popupRef && this.overlayRef) {
      this.applyPopupInputs(this.popupRef.instance);
      this.popupRef.instance.mobile = true;
      this.popupRef.instance.hideArrow = true;
      this.popupRef.changeDetectorRef.detectChanges();
      return;
    }

    const imgRect = this.baseDrawer?.imageContainer?.nativeElement?.getBoundingClientRect();
    // Sit just below the image, but never so low the drawer falls off-screen (the image can be
    // near full-height on a phone) — cap the start so the panel stays reachable.
    const below = Math.max(0, Math.round(imgRect?.bottom ?? 0));
    const top = Math.min(below, Math.round(window.innerHeight * 0.4));

    const positionStrategy = this.overlay.position().global().right('0').top(`${top}px`);
    this.popupPositionStrategy = null;

    // No backdrop: a backdrop would swallow the tap's follow-up click and close the drawer
    // immediately. The boxes stay tappable (tap another to switch), and the popup's own
    // close button dismisses it.
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    this.popupRef = this.overlayRef.attach(new ComponentPortal(AnnotationPopupComponent));
    const instance = this.popupRef.instance;
    this.applyPopupInputs(instance);
    instance.mobile = true;
    instance.hideArrow = true;
    this.popupRef.changeDetectorRef.detectChanges();

    this.popupSubs = [
      instance.closed.subscribe(() => this.destroyPopup()),
      instance.deleteTag.subscribe(() => { const i = this.activeIndex; this.destroyPopup(); if (i != null) this.onBoxDeleted(i); }),
      instance.editTag.subscribe(() => this.onEditTag()),
      instance.fieldsChange.subscribe((fields: OptionalField[]) => this.onFieldsChange(fields)),
      instance.commentChange.subscribe((c: string) => this.onCommentChange(c)),
      instance.labelChanged.subscribe((labelId: string) => this.onLabelChanged(labelId)),
    ];
  }

  private destroyPopup(): void {
    this.popupSubs.forEach(s => s.unsubscribe());
    this.popupSubs = [];
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.popupRef = null;
    this.popupPositionStrategy = null;
  }

  async save(): Promise<boolean> {
    const mainProject = this.labelProject;
    if (!mainProject) return false;
    try {
      this.saveActiveFields(); // flush the current popup edits into the active tag

      // every tag must have a label
      if (this.tags.some(t => !t.label))
        throw new Error(this.translate.instant("stream.error.no_label"));

      // criticity required on each main-project tag (when configured required)
      const requiredCriticity = (mainProject.optionalFields || []).some((f: any) =>
        f.label === 'criticity' && f.required && (!Array.isArray(f.labelOption?.values) || f.labelOption?.values?.length));
      if (requiredCriticity && this.tags.some(t => this.isRiskTag(t) && t.probability == null && !t.optionalFields.find(of => of.key === 'criticity')))
        throw new Error(this.translate.instant("stream.error.no_fields", { fields: 'criticity' }));

      const isRiskUpdate = !!this.imageStatus?.history?.find(status => status.action == "labeled") && !!this.imageStatus?.labels?.length;

      const toTag = (l: StatusLabel): Tag => {
        const [left = 0, top = 0, width = 0, height = 0] = l.boundingBox || [];
        return {
          _id: l._id,
          tagName: l.name,
          probability: l.probability ?? 1,
          boundingBox: { left, top, width, height },
          author: l.author,
          date: l.createdDate || new Date().toISOString(),
          // attachment tags carry { key, value } only
          optionalFields: (l.optionalFields || []).map(f => ({ key: f.key, value: f.value }))
        };
      };

      // One status, one tags[module] and one labelUpdate per label project. tags[module] is replaced
      // wholesale server-side, so the AI predictions not shown here are re-sent untouched.
      const tags: Record<string, Tag[]> = {};
      const labelUpdates: LabelUpdate[] = [];
      for (const labelProject of this.labelProjects) {
        const status = this.statusOf(labelProject);
        const projectTags = this.tags.filter(t => this.moduleOf(t) === labelProject.name);
        if (!status && projectTags.length) throw new Error(`No ${labelProject.name} status for this image yet, please retry`);
        if (!status || (labelProject !== mainProject && !projectTags.length && !status.labels?.length)) continue;

        const statusLabels: StatusLabel[] = projectTags.filter(t => t.probability == null).map(t => {
          if (!t._id) t._id = this.genObjectId();
          const fields = [...t.optionalFields];
          if (t.comment?.trim()) fields.push({ key: 'commentaire', value: [t.comment.trim()] });
          return {
            _id: t._id,
            name: t.label || '',
            author: t.author || this.user.email,
            createdDate: new Date().toISOString(),
            boundingBox: [t.box.left, t.box.top, t.box.width, t.box.height],
            probability: 1,
            optionalFields: fields
          };
        });
        const aiLabels: StatusLabel[] = [
          ...projectTags.filter(t => t.probability != null).map(t => ({
            _id: t._id,
            name: t.label || '',
            probability: t.probability,
            boundingBox: [t.box.left, t.box.top, t.box.width, t.box.height],
          })),
          ...(status.aiLabels || []).filter(l => !this.isShownAi(l, labelProject)),
        ];
        tags[labelProject.name] = [
          ...statusLabels.map(l => ({ ...toTag(l), author: this.user.email })),
          ...aiLabels.map(toTag),
        ];
        labelUpdates.push({ labelProject: labelProject.name, action: "labeled", labels: tags[labelProject.name]!, optionalFields: [] });

        const statusIndex = this.imageStatusList.findIndex(s => s.id == status.id);
        // root optionalFields are legacy now
        this.imageStatusList.splice(statusIndex, 1, { ...structuredClone(status), action: "label", labels: statusLabels, aiLabels, optionalFields: [], fromHse: true });
      }

      const attachmentQuery: PutAttachment = {
        id: this.image.attachment!,
        tags,
        tracker: this.image.tracker,
        createdDate: this.image.createdDate,
        labelUpdates,
        isRiskUpdate,
        project: this.image.project as string,
        database: this.image?.database,
        url: this.image.url as string,
        processed: this.image.processed,
        fromHse: true
      };

      return await new Promise<boolean>((resolve) => {
        this.attachmentService.setAttachmentLabel(attachmentQuery)
          .pipe(first())
          .subscribe({
            next: async () => {
              try {
                await this.labelAction();
                this.tags.forEach(t => t.isNew = false);
                if (tags['worker']) this.imageInfo.refreshImage?.(this.image.id);
                this.pop.success(this.translate.instant(`stream.risk.${this.tags.length ? 'success' : 'success_delete'}`));
                this.hasChange = false;
                this.cdr.detectChanges();
                resolve(true);
              } catch (err: any) {
                this.pop.error(err.message?.toString() || err.toString());
                resolve(false);
              }
            },
            error: (err) => {
              this.pop.error(err.message?.toString() || err.toString());
              resolve(false);
            }
          });
      });
    } catch (err: any) {
      this.pop.error(err.message.toString());
      console.error('Erreur lors du tag:', err.toString());
      return false;
    }
  }

  async saveAndClose(): Promise<void> {
    const ok = await this.save();
    if (ok) this.dialogRef.close();
  }

  private hasPendingChanges(): boolean {
    return this.hasChange;
  }

  reportRisk() {
    if (!this.risk) return;
    const dialogRef = this.dialog.open(RiskReportComponent, {
      width: '600px',
      data: { risk: this.risk }
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res?.ok && res.comment) {
        this.risk!.payload.comment = res.comment;
        this.risk!.payload["status"] = res.status;
        this.metricService.reportRisk(this.risk!).pipe(first()).subscribe(() => {
          this.pop.toast(this.translate.instant('risks.report_save'), 'success', 4000, 'save');
        });

        // apply the report comment to the active tag and reflect it in the open popup in realtime
        this.comment = res.comment;
        const tag = this.activeTag;
        if (tag) tag.comment = res.comment;
        if (this.popupRef) {
          this.popupRef.instance.comment = this.comment;
          this.popupRef.changeDetectorRef.detectChanges();
        }
        this.hasChange = true;
      }
    });
  }

  close() {
    this.dialogRef.close()
  }

  labelAction(): Promise<void> {
    return new Promise((resolve, reject) => {
      let updatedStatuses = this.imageStatusList?.filter(status => status.action);

      if (!updatedStatuses?.length) {
        return reject(new Error("Aucun status à mettre à jour"));
      }

      this.imageStatusService.updateStatuses(updatedStatuses)
        .pipe(first())
        .subscribe({
          next: (res: any) => {
            if (res.statuses?.length) {
              let statuses = res.statuses;
              for (let status of statuses) {
                let statusIndex = this.imageStatusList.findIndex(imgStatus => imgStatus.id == status.id);
                if (statusIndex >= 0) {
                  this.imageStatusList[statusIndex] = status;
                } else {
                  this.imageStatusList.push(status);
                }
              }
              this.image.statuses = structuredClone(this.imageStatusList);
            }

            if (res.errors?.length) {
              console.error("Errors while updating statuses :>>", res.errors);
            }

            this.cdr.detectChanges();
            resolve();
          },
          error: (err) => {
            reject(err);
          }
        });
    });
  }

  onLabelChanged(labelId: string) {
    this.keepHoverPreview();
    const label = this.labels.find(label => label.id == labelId);
    if (label && label.name === this.selectedLabel()?.name) return;
    this.applyLabel(label);
    this.hasChange = true;
    this.saveActiveFields();
    this.refreshPopupIfOpen();
  }

  onFieldsChange(fields: OptionalField[]): void {
    this.keepHoverPreview();
    this.optionalFields = fields.filter(field => field.value.length && field.value[0]);
    this.saveActiveFields();
    this.hasChange = true;
  }

  onCommentChange(comment: string): void {
    this.keepHoverPreview();
    this.comment = comment;
    this.saveActiveFields();
    this.hasChange = true;
  }

  onBoxDeleted(index: number) {
    this.keepHoverPreview();
    this.tags.splice(index, 1);
    this.tags = [...this.tags];
    if (this.activeIndex === index) { this.destroyPopup(); this.clearActiveFields(); }
    else if (this.activeIndex != null && this.activeIndex > index) { this.activeIndex--; }
    // "delete all": no boxes left but the image had labels → signal a clearing save
    if (this.imageStatus?.labels?.length && !this.tags.length) this.deletedTag = true;
    this.hasChange = true;
  }

  /** Select a risk from the "Risques détectés" fieldset (and open its editor). */
  selectTag(index: number) {
    this.onBoxOpened(index);
  }

  async nextImage() {
    if (this.hasPendingChanges()) {
      const ok = await this.save();
      if (!ok) return;
    }
    this.optFields?.reset();
    this.init(await this.imageInfo.getImageInfo("next"));
  }

  async prevImage() {
    if (this.hasPendingChanges()) {
      const ok = await this.save();
      if (!ok) return;
    }
    this.init(await this.imageInfo.getImageInfo("prev"));
  }

  private get armsNextBox(): boolean {
    return this.activeIndex == null || !!this.baseDrawer?.isDrawingBox;
  }

  isSelected(label: Label): boolean {
    const selected = this.armsNextBox ? this.armedLabel() : this.selectedLabel();
    return !!selected && selected.id === label.id;
  }

  // From the "Liste des labels" list: relabels the active box, or with no box selected (or one
  // being drawn) picks the label the next drawn box gets. Clicking the armed label again clears it.
  selectLabel(label: Label | undefined, toggle = false) {
    if (!this.labelProject || !label) return;
    if (this.armsNextBox) {
      this.armedLabel.set(toggle && this.armedLabel()?.id === label.id ? undefined : label);
      this.cdr.detectChanges();
      return;
    }
    this.keepHoverPreview();
    this.armedLabel.set(label);
    if (label !== this.selectedLabel()) {
      this.hasChange = true;
      this.applyLabel(label);
      this.saveActiveFields();
      this.refreshPopupIfOpen();
    }
    this.cdr.detectChanges();
  }

  private applyLabel(label?: Label) {
    const relabel = this.selectedLabel()?.name !== label?.name;
    this.selectedLabel.set(label);
    this.applyDefaultCriticity(label);
    this.applyDynamicDefaults(label, relabel, !!this.activeTag?.isNew);
  }

  private applyDynamicDefaults(label: Label | undefined, relabel: boolean, stamp: boolean) {
    if (!label) return;
    const labelProject = this.labelProjects.find(lp => lp.name === label.module) ?? this.labelProject;
    const dynamicKeys = (labelProject?.optionalFields || []).filter(f => f.type === 'dynamic').map(f => f.label);
    if (!dynamicKeys.length) return;
    const accepted = label.optionalFields || [];
    const defaults = (label.defaultOptionalFields || []).filter(v => accepted.includes(v));
    const fields = relabel ? this.optionalFields.filter(f => !dynamicKeys.includes(f.key)) : [...this.optionalFields];
    for (const key of dynamicKeys) {
      if (stamp && defaults.length && !fields.some(f => f.key === key)) fields.push({ key, value: [...defaults] });
    }
    this.optionalFields = fields;
  }

  private applyDefaultCriticity(label?: Label) {
    if (label?.module && label.module !== this.labelProject?.name) return;
    const existing = this.optionalFields.find(f => f.key === 'criticity');
    const defaultValue = label?.name === 'Bonne pratique' ? 'Bonne pratique' : 'Faible';
    // Only (re)set when there's no criticity yet, or the "Bonne pratique" special case flips.
    const needsReset = !existing
      || (existing.value[0] !== 'Bonne pratique' && label?.name === 'Bonne pratique')
      || (existing.value[0] === 'Bonne pratique' && label?.name !== 'Bonne pratique');
    if (!needsReset) return;
    // Replace (not append) so OptionalFieldsComponent doesn't read a stale duplicate.
    this.optionalFields = [
      ...this.optionalFields.filter(f => f.key !== 'criticity'),
      { key: 'criticity', value: [defaultValue] }
    ];
  }

  applyFilter(searchText: string) {
    this.searchText = searchText.toLowerCase();

    if (this.labels) {
      const filtered = this.labels.filter(label =>
        label.name.toLowerCase().includes(this.searchText)
      );
      this.visibleLabels.set(filtered);
    }
  }

  onEnterPressed() {
    const labels = this.visibleLabels();
    if (labels.length > 0 && this.labelProject) {
      this.selectLabel(labels[0]);
    }
  }

  async onInputBlur() {
    await new Promise(res => setTimeout(res, 100));
    const labels = this.visibleLabels();
    if (labels.length === 1 && this.labelProject) {
      this.selectLabel(labels[0]);
    }
    this.visibleLabels.set(this.labels);
  }

  getLabelColor(labelName: string): string {
    return this.labelColorMap()[labelName] || '#ff0000';
  }

  getLabelBackground(color: string, isSelected: boolean = false): string {
    if (isSelected) {
      return `linear-gradient(to right, ${color} 100%, ${this.tools.hexToRGBA(color, 0.3)} 100%)`;
    }
    return `linear-gradient(to right, ${color} 5px, ${this.tools.hexToRGBA(color, 0.3)} 5px)`;
  }

  private buildLabelColorMap() {
    const colorMap: Record<string, string> = {};
    for (const label of this.labels) {
      colorMap[label.name] = label.color || '#FF0000';
    }
    this.labelColorMap.set(colorMap);
  }

  getDarkerColor(hex: string, factor: number = 0.5): string {
    return this.tools.getDarkerColor(hex, factor);
  }
}
